import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { map, catchError, tap, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';

/**
 * Interface for rate detail from backend
 */
interface RateDetail {
    rate: number;
    updatedAt: string;
    decimalPoints: number;
    symbolAr?: string; // Arabic symbol for this currency (e.g., "ريال" for SAR)
}

/**
 * Interface for the rates state stored in service
 */
interface RatesState {
    baseCurrency: string;
    baseCurrencyArabicSymbol?: string; // Arabic symbol for base currency (e.g., "د.ب" for BHD)
    rates: { [currencyCode: string]: RateDetail };
    lastUpdated: string;
    recordId: string;
}

/**
 * Interface for API response
 */
interface UserRatesResponse {
    code: number;
    data?: RatesState;
    msg?: string;
}

/**
 * CurrencyRateService
 *
 * Manages exchange rates for currency conversion on the customer-facing web application.
 *
 * Features:
 * - Lazy loading: Rates loaded on first request
 * - In-memory caching: Single fetch per session
 * - LocalStorage backup: Instant load on page refresh
 * - Reactive updates: BehaviorSubject for rate changes
 * - Automatic refresh: Optional background refresh
 * - Direct conversions: No chained lookups
 *
 * Usage:
 * ```typescript
 * constructor(private currencyRateService: CurrencyRateService) {}
 *
 * // Convert amount
 * const usdAmount = this.currencyRateService.convert(100, 'SAR', 'USD');
 *
 * // Listen to rate changes
 * this.currencyRateService.rates$.subscribe(rates => { ... });
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class CurrencyRateService {
    // Configuration
    private readonly CACHE_KEY = 'currency-rates-cache';
    private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes for localStorage cache
    private readonly REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes auto-refresh
    private readonly STALE_THRESHOLD = 30 * 60 * 1000; // 30 minutes = stale

    // State management
    private ratesState: RatesState | null = null;
    private ratesSubject$ = new BehaviorSubject<RatesState | null>(null);
    private loadingSubject$ = new BehaviorSubject<boolean>(false);
    private errorSubject$ = new BehaviorSubject<string | null>(null);

    // Cache for conversion results to improve performance
    private conversionCache = new Map<string, number>();

    // Observable streams
    public readonly rates$ = this.ratesSubject$.asObservable();
    public readonly loading$ = this.loadingSubject$.asObservable();
    public readonly error$ = this.errorSubject$.asObservable();

    // Auto-refresh timer
    private refreshTimer: any = null;

    constructor(
        private http: HttpClient,
        @Inject(PLATFORM_ID) private platformId: Object
    ) {
        // Initialize: Try to load from localStorage on browser
        if (isPlatformBrowser(this.platformId)) {
            this.initializeFromCache();
        }
    }

    /**
     * Initialize rates from localStorage cache if available and fresh
     */
    private initializeFromCache(): void {
        const cached = this.loadFromLocalStorage();
        if (cached) {
            this.ratesState = cached;
            this.ratesSubject$.next(cached);
        }
    }

    /**
     * Load rates from backend API
     * This is called lazily on first conversion request or explicitly
     */
    public loadRates(forceRefresh: boolean = false): Observable<RatesState> {
        // If we have fresh rates in memory and not forcing refresh, return cached
        if (!forceRefresh && this.ratesState && !this.isStale()) {
            return of(this.ratesState);
        }

        // If already loading, wait for current request
        if (this.loadingSubject$.value) {
            return this.rates$.pipe(
                map(rates => {
                    if (!rates) {
                        throw new Error('Failed to load rates');
                    }
                    return rates;
                })
            );
        }

        this.loadingSubject$.next(true);
        this.errorSubject$.next(null);

        // Add timestamp to prevent browser/proxy caching
        const timestamp = new Date().getTime();
        const url = `${environment.apiUrl}/currency-rates/user-rates?_t=${timestamp}`;

        return this.http
            .get<UserRatesResponse>(url, {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                }
            })
            .pipe(
                tap(rawResponse => {}),
                map(response => {
                    if (response.code !== 200 || !response.data) {
                        throw new Error(
                            response.msg || 'Failed to load currency rates'
                        );
                    }
                    return response.data;
                }),
                tap(rates => {
                    // Update state
                    this.ratesState = rates;
                    this.ratesSubject$.next(rates);

                    // Clear conversion cache on new rates
                    this.conversionCache.clear();

                    // Persist to localStorage
                    this.persistToLocalStorage(rates);

                    // Setup auto-refresh
                    this.setupAutoRefresh();

                    this.loadingSubject$.next(false);
                }),
                catchError(error => {
                    console.error(
                        '[CurrencyRateService] Error loading rates:',
                        error
                    );
                    this.errorSubject$.next(
                        error.message || 'Failed to load currency rates'
                    );
                    this.loadingSubject$.next(false);

                    // Try to use cached rates as fallback
                    if (this.ratesState) {
                        console.warn(
                            '[CurrencyRateService] Using stale cached rates as fallback'
                        );
                        return of(this.ratesState);
                    }

                    // RxJS v6 style: throw the actual error object (not a factory)
                    return throwError(error);
                }),
                shareReplay(1)
            );
    }

    /**
     * Convert amount from one currency to another using direct rates
     * @param amount - Amount to convert
     * @param fromCurrency - Source currency code (e.g., "SAR")
     * @param toCurrency - Target currency code (e.g., "USD") - defaults to user's selected currency
     * @returns Converted amount rounded to the appropriate decimal points
     */
    public convert(
        amount: number,
        fromCurrency: string,
        toCurrency?: string
    ): number {
        if (!amount || amount <= 0) {
            return 0;
        }

        // Ensure rates are loaded
        if (!this.ratesState) {
            console.warn(
                '[CurrencyRateService] Rates not loaded yet, returning original amount'
            );
            // Trigger load in background
            this.loadRates().subscribe();
            return amount;
        }

        // Default to base currency if target not specified
        const targetCurrency = toCurrency || this.ratesState.baseCurrency;

        // Same currency, no conversion needed
        if (fromCurrency === targetCurrency) {
            return amount;
        }

        // Check conversion cache
        const cacheKey = `${amount}:${fromCurrency}:${targetCurrency}`;
        if (this.conversionCache.has(cacheKey)) {
            return this.conversionCache.get(cacheKey)!;
        }

        // Get the direct rate from source currency to target currency
        const rateInfo = this.ratesState.rates[fromCurrency];

        if (!rateInfo) {
            console.warn(
                `[CurrencyRateService] No rate found for currency: ${fromCurrency}`
            );
            return amount; // Return original amount as fallback
        }

        // Perform conversion
        const convertedAmount = amount * rateInfo.rate;

        // Round to the appropriate decimal points for the TARGET currency (not source!)
        // The decimalPoints in the API response represent each currency's own decimal points
        const targetDecimalPoints = this.getDecimalPoints(targetCurrency) ?? 2;
        const roundedAmount = this.roundToDecimalPoints(
            convertedAmount,
            targetDecimalPoints
        );

        // Cache the result
        this.conversionCache.set(cacheKey, roundedAmount);

        return roundedAmount;
    }

    /**
     * Get the current rate for a specific currency conversion
     * @param fromCurrency - Source currency code
     * @param toCurrency - Target currency code (optional, defaults to user's currency)
     * @returns The exchange rate or null if not available
     */
    public getRate(fromCurrency: string, toCurrency?: string): number | null {
        if (!this.ratesState) {
            return null;
        }

        const targetCurrency = toCurrency || this.ratesState.baseCurrency;

        // Same currency
        if (fromCurrency === targetCurrency) {
            return 1.0;
        }

        const rateInfo = this.ratesState.rates[fromCurrency];
        return rateInfo ? rateInfo.rate : null;
    }

    /**
     * Get the decimal points for a specific currency
     * @param currencyCode - Currency code
     * @returns Number of decimal points or null if not available
     */
    public getDecimalPoints(currencyCode: string): number | null {
        if (!this.ratesState) {
            return null;
        }

        // Check if currency exists in rates (includes base currency too)
        const rateInfo = this.ratesState.rates[currencyCode];
        if (rateInfo && rateInfo.decimalPoints !== undefined) {
            return rateInfo.decimalPoints;
        }

        // Fallback: if it's the base currency but not in rates object
        if (currencyCode === this.ratesState.baseCurrency) {
            return 2; // Default fallback
        }

        return null;
    }

    /**
     * Check if a rate exists for a specific currency
     * @param currencyCode - Currency code to check
     * @returns True if rate exists
     */
    public hasRate(currencyCode: string): boolean {
        if (!this.ratesState) {
            return false;
        }

        return (
            currencyCode === this.ratesState.baseCurrency ||
            !!this.ratesState.rates[currencyCode]
        );
    }

    /**
     * Get the user's current display currency
     * @returns Base currency code or null if not loaded
     */
    public getBaseCurrency(): string | null {
        return this.ratesState?.baseCurrency || null;
    }

    /**
     * Get the Arabic symbol for the user's current display currency
     * @returns Arabic symbol (e.g., "د.ب" for BHD) or null if not available
     */
    public getBaseCurrencyArabicSymbol(): string | null {
        return this.ratesState?.baseCurrencyArabicSymbol || null;
    }

    /**
     * Get the Arabic symbol for any currency
     * @param currencyCode - Currency code (e.g., "SAR", "BHD")
     * @returns Arabic symbol (e.g., "ريال" for SAR) or null if not available
     */
    public getArabicSymbol(currencyCode: string): string | null {
        if (!this.ratesState) {
            return null;
        }

        // Check if it's the base currency first
        if (currencyCode === this.ratesState.baseCurrency) {
            return this.ratesState.baseCurrencyArabicSymbol || null;
        }

        // Otherwise, check in the rates object
        const rateDetail = this.ratesState.rates[currencyCode];
        return rateDetail?.symbolAr || null;
    }

    /**
     * Get cached rates synchronously (for checking cache validity)
     * @returns Current rates state or null if not loaded
     */
    public getCachedRatesSync(): RatesState | null {
        return this.ratesState;
    }

    /**
     * Force refresh rates from backend
     * Useful for manual refresh button
     */
    public refreshRates(): Observable<RatesState> {
        return this.loadRates(true);
    }

    /**
     * Clear all cached data (memory + localStorage)
     * Call this when user changes their display currency
     */
    public clearCache(): void {
        this.ratesState = null;
        this.ratesSubject$.next(null);
        this.conversionCache.clear();
        this.cancelAutoRefresh();

        if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(this.CACHE_KEY);
        }
    }

    /**
     * Check if current rates are stale (older than threshold)
     * @returns True if rates are stale or not loaded
     */
    public isStale(): boolean {
        if (!this.ratesState || !this.ratesState.lastUpdated) {
            return true;
        }

        const lastUpdated = new Date(this.ratesState.lastUpdated).getTime();
        const now = Date.now();
        const age = now - lastUpdated;

        return age > this.STALE_THRESHOLD;
    }

    /**
     * Get the age of current rates in minutes
     * @returns Age in minutes or null if not loaded
     */
    public getRateAge(): number | null {
        if (!this.ratesState || !this.ratesState.lastUpdated) {
            return null;
        }

        const lastUpdated = new Date(this.ratesState.lastUpdated).getTime();
        const now = Date.now();
        const ageMs = now - lastUpdated;

        return Math.floor(ageMs / 60000); // Convert to minutes
    }

    /**
     * Persist rates to localStorage for faster subsequent loads
     */
    private persistToLocalStorage(rates: RatesState): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        try {
            const cacheData = {
                rates,
                cachedAt: Date.now()
            };
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error(
                '[CurrencyRateService] Failed to persist to localStorage:',
                error
            );
        }
    }

    /**
     * Load rates from localStorage if cache is still fresh
     */
    private loadFromLocalStorage(): RatesState | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        try {
            const cached = localStorage.getItem(this.CACHE_KEY);
            if (!cached) {
                return null;
            }

            const cacheData = JSON.parse(cached);
            const age = Date.now() - cacheData.cachedAt;

            // Check if cache is still fresh
            if (age > this.CACHE_TTL) {
                localStorage.removeItem(this.CACHE_KEY);
                return null;
            }

            return cacheData.rates;
        } catch (error) {
            console.error(
                '[CurrencyRateService] Failed to load from localStorage:',
                error
            );
            return null;
        }
    }

    /**
     * Setup automatic background refresh
     */
    private setupAutoRefresh(): void {
        // Cancel existing timer
        this.cancelAutoRefresh();

        // Only setup auto-refresh in browser
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        // Setup new timer
        this.refreshTimer = timer(this.REFRESH_INTERVAL, this.REFRESH_INTERVAL)
            .pipe(switchMap(() => this.loadRates(true)))
            .subscribe(() => error =>
                console.error(
                    '[CurrencyRateService] Auto-refresh failed:',
                    error
                )
            );
    }

    /**
     * Cancel automatic refresh timer
     */
    private cancelAutoRefresh(): void {
        if (this.refreshTimer) {
            this.refreshTimer.unsubscribe();
            this.refreshTimer = null;
        }
    }

    /**
     * Round a number to a specific number of decimal points
     * @param value - The number to round
     * @param decimalPoints - Number of decimal places (0-3)
     * @returns Rounded number
     */
    private roundToDecimalPoints(value: number, decimalPoints: number): number {
        if (decimalPoints < 0 || decimalPoints > 3) {
            decimalPoints = 2; // Default to 2 if invalid
        }

        const multiplier = Math.pow(10, decimalPoints);
        return Math.round(value * multiplier) / multiplier;
    }

    /**
     * Cleanup on service destroy
     */
    public ngOnDestroy(): void {
        this.cancelAutoRefresh();
    }
}
